import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, interval, of } from 'rxjs';
import { switchMap, takeUntil, catchError, distinctUntilChanged, filter } from 'rxjs/operators';
import { TicketProvisioningService } from './ticket-provisioning.service';
import { ProvisioningStatusResponse } from '../interface/avd-provisioning';

/**
 * Configuration for status polling
 */
export interface PollingConfig {
  intervalMs: number; // Polling interval in milliseconds
  maxAttempts?: number; // Maximum number of polling attempts (0 = infinite)
  stopOnSuccess?: boolean; // Stop polling when status is 'Success'
  stopOnFailure?: boolean; // Stop polling when status is 'Failed'
}

/**
 * Service for polling AVD provisioning status
 * Provides automatic status updates at configured intervals
 */
@Injectable({
  providedIn: 'root'
})
export class AvdStatusPollingService implements OnDestroy {
  private readonly defaultConfig: PollingConfig = {
    intervalMs: 30000, // 30 seconds
    maxAttempts: 0, // Infinite
    stopOnSuccess: true,
    stopOnFailure: false // Continue polling on failure to allow retry
  };

  // Active polling sessions keyed by ticketId
  private pollingSubjects = new Map<string, BehaviorSubject<ProvisioningStatusResponse | null>>();
  private stopPollingSubjects = new Map<string, Subject<void>>();
  private attemptCounters = new Map<string, number>();

  constructor(private provisioningService: TicketProvisioningService) { }

  /**
   * Start polling for a ticket's provisioning status
   */
  startPolling(
    ticketId: string,
    config?: Partial<PollingConfig>
  ): Observable<ProvisioningStatusResponse | null> {
    // If already polling this ticket, return existing observable
    if (this.pollingSubjects.has(ticketId)) {
      return this.pollingSubjects.get(ticketId)!.asObservable();
    }

    const finalConfig = { ...this.defaultConfig, ...config };
    const statusSubject = new BehaviorSubject<ProvisioningStatusResponse | null>(null);
    const stopSubject = new Subject<void>();

    this.pollingSubjects.set(ticketId, statusSubject);
    this.stopPollingSubjects.set(ticketId, stopSubject);
    this.attemptCounters.set(ticketId, 0);

    console.log(`[AVD Polling] Starting polling for ticket ${ticketId}`, finalConfig);

    // Initial immediate fetch
    this.fetchStatus(ticketId, statusSubject);

    // Start interval polling
    interval(finalConfig.intervalMs)
      .pipe(
        takeUntil(stopSubject),
        switchMap(() => {
          const attempts = this.attemptCounters.get(ticketId) || 0;
          this.attemptCounters.set(ticketId, attempts + 1);

          // Check max attempts
          if (finalConfig.maxAttempts && finalConfig.maxAttempts > 0) {
            if (attempts >= finalConfig.maxAttempts) {
              console.log(`[AVD Polling] Max attempts reached for ticket ${ticketId}`);
              this.stopPolling(ticketId);
              return of(null);
            }
          }

          return this.provisioningService.getProvisioningStatus(ticketId).pipe(
            catchError(error => {
              console.error(`[AVD Polling] Error fetching status for ${ticketId}:`, error);
              return of(null);
            })
          );
        }),
        filter(status => status !== null),
        distinctUntilChanged((prev, curr) => {
          // Only emit if status actually changed
          if (!prev || !curr) return false;
          return prev.status === curr.status &&
                 prev.attemptNumber === curr.attemptNumber &&
                 prev.completedUtc === curr.completedUtc;
        })
      )
      .subscribe({
        next: (status) => {
          if (status) {
            console.log(`[AVD Polling] Status update for ${ticketId}:`, status.status);
            statusSubject.next(status);

            // Auto-stop on terminal states if configured
            if (finalConfig.stopOnSuccess && status.status === 'Success') {
              console.log(`[AVD Polling] Success detected, stopping polling for ${ticketId}`);
              this.stopPolling(ticketId);
            } else if (finalConfig.stopOnFailure && status.status === 'Failed') {
              console.log(`[AVD Polling] Failure detected, stopping polling for ${ticketId}`);
              this.stopPolling(ticketId);
            }
          }
        },
        error: (error) => {
          console.error(`[AVD Polling] Polling error for ${ticketId}:`, error);
          this.stopPolling(ticketId);
        }
      });

    return statusSubject.asObservable();
  }

  /**
   * Stop polling for a specific ticket
   */
  stopPolling(ticketId: string): void {
    console.log(`[AVD Polling] Stopping polling for ticket ${ticketId}`);

    const stopSubject = this.stopPollingSubjects.get(ticketId);
    if (stopSubject) {
      stopSubject.next();
      stopSubject.complete();
    }

    const statusSubject = this.pollingSubjects.get(ticketId);
    if (statusSubject) {
      statusSubject.complete();
    }

    this.pollingSubjects.delete(ticketId);
    this.stopPollingSubjects.delete(ticketId);
    this.attemptCounters.delete(ticketId);
  }

  /**
   * Stop all active polling sessions
   */
  stopAllPolling(): void {
    console.log('[AVD Polling] Stopping all polling sessions');
    const ticketIds = Array.from(this.pollingSubjects.keys());
    ticketIds.forEach(ticketId => this.stopPolling(ticketId));
  }

  /**
   * Check if currently polling a ticket
   */
  isPolling(ticketId: string): boolean {
    return this.pollingSubjects.has(ticketId);
  }

  /**
   * Get current status for a ticket without starting polling
   */
  getCurrentStatus(ticketId: string): Observable<ProvisioningStatusResponse | null> {
    return this.provisioningService.getProvisioningStatus(ticketId).pipe(
      catchError(error => {
        console.error(`[AVD Polling] Error getting current status for ${ticketId}:`, error);
        return of(null);
      })
    );
  }

  /**
   * Refresh status immediately (useful for retry scenarios)
   */
  refreshStatus(ticketId: string): void {
    const statusSubject = this.pollingSubjects.get(ticketId);
    if (statusSubject) {
      this.fetchStatus(ticketId, statusSubject);
    }
  }

  /**
   * Get the number of polling attempts for a ticket
   */
  getAttemptCount(ticketId: string): number {
    return this.attemptCounters.get(ticketId) || 0;
  }

  /**
   * Reset attempt counter for a ticket
   */
  resetAttemptCount(ticketId: string): void {
    this.attemptCounters.set(ticketId, 0);
  }

  /**
   * Private helper to fetch status and emit
   */
  private fetchStatus(
    ticketId: string,
    statusSubject: BehaviorSubject<ProvisioningStatusResponse | null>
  ): void {
    this.provisioningService.getProvisioningStatus(ticketId).pipe(
      catchError(error => {
        console.error(`[AVD Polling] Initial fetch error for ${ticketId}:`, error);
        return of(null);
      })
    ).subscribe(status => {
      if (status) {
        statusSubject.next(status);
      }
    });
  }

  /**
   * Cleanup on service destroy
   */
  ngOnDestroy(): void {
    this.stopAllPolling();
  }
}























